import { useCallback, useEffect, useRef, useState } from 'react';
import {
  collection,
  collectionGroup,
  doc,
  getCountFromServer,
  getDocs,
  limit,
  orderBy,
  query,
  QueryDocumentSnapshot,
  startAfter,
  where,
  type DocumentData
} from 'firebase/firestore';
import { db } from '../backend/firebase';
import { ContentReport, ReportEvent } from '../backend/moderationService';

const REPORT_PAGE_SIZE = 20;

function reportFromSnapshot(snapshot: QueryDocumentSnapshot<DocumentData>): ContentReport {
  return { id: snapshot.id, ...snapshot.data() } as ContentReport;
}

export function useReports(uid: string | undefined) {
  const [received, setReceived] = useState<ContentReport[]>([]);
  const [filed, setFiled] = useState<ContentReport[]>([]);
  const [timeline, setTimeline] = useState<ReportEvent[]>([]);
  const [receivedLoading, setReceivedLoading] = useState(false);
  const [filedLoading, setFiledLoading] = useState(false);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [receivedHasMore, setReceivedHasMore] = useState(true);
  const [filedHasMore, setFiledHasMore] = useState(true);
  const [timelineHasMore, setTimelineHasMore] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [receivedCount, setReceivedCount] = useState(0);
  const [filedCount, setFiledCount] = useState(0);
  const [openReceivedCount, setOpenReceivedCount] = useState(0);
  const receivedCursor = useRef<QueryDocumentSnapshot<DocumentData> | null>(null);
  const filedCursor = useRef<QueryDocumentSnapshot<DocumentData> | null>(null);
  const timelineCursor = useRef<QueryDocumentSnapshot<DocumentData> | null>(null);
  const busy = useRef(new Set<string>());

  useEffect(() => {
    setReceived([]);
    setFiled([]);
    setTimeline([]);
    setReceivedHasMore(true);
    setFiledHasMore(true);
    setTimelineHasMore(true);
    receivedCursor.current = null;
    filedCursor.current = null;
    timelineCursor.current = null;
    setError(null);
    setReceivedCount(0);
    setFiledCount(0);
    setOpenReceivedCount(0);
    if (!uid) {
      setReceivedCount(0);
      setFiledCount(0);
      setOpenReceivedCount(0);
      return;
    }
    void Promise.all([
      getCountFromServer(collection(db, 'content_reports_received', uid, 'reports')),
      getCountFromServer(query(collection(db, 'content_reports'), where('reporterUid', '==', uid))),
      getCountFromServer(query(
        collection(db, 'content_reports_received', uid, 'reports'),
        where('status', '==', 'open')
      ))
    ]).then(([receivedSnapshot, filedSnapshot, openSnapshot]) => {
      setReceivedCount(receivedSnapshot.data().count);
      setFiledCount(filedSnapshot.data().count);
      setOpenReceivedCount(openSnapshot.data().count);
    }).catch(countError => {
      setError(countError instanceof Error ? countError : new Error('Could not count reports.'));
    });
  }, [uid]);

  const loadPage = useCallback(async (kind: 'received' | 'filed' | 'timeline') => {
    if (!uid || busy.current.has(kind)) return;
    const cursor = kind === 'received' ? receivedCursor.current : kind === 'filed' ? filedCursor.current : timelineCursor.current;
    const more = kind === 'received' ? receivedHasMore : kind === 'filed' ? filedHasMore : timelineHasMore;
    if (!more) return;
    busy.current.add(kind);
    setError(null);
    if (kind === 'received') setReceivedLoading(true);
    else if (kind === 'filed') setFiledLoading(true);
    else setTimelineLoading(true);
    try {
      const base = kind === 'timeline'
        ? query(
            collectionGroup(db, 'events'),
            where('contentOwnerUid', '==', uid),
            orderBy('createdAt', 'desc')
          )
        : kind === 'received'
          ? query(
              collection(db, 'content_reports_received', uid, 'reports'),
              orderBy('reportedAt', 'desc')
            )
          : query(
              collection(db, 'content_reports'),
              where('reporterUid', '==', uid),
              orderBy('reportedAt', 'desc')
            );
      const reportQuery = query(
        base,
        ...(cursor ? [startAfter(cursor)] : []),
        limit(REPORT_PAGE_SIZE)
      );
      const snapshot = await getDocs(reportQuery);
      const last = snapshot.docs[snapshot.docs.length - 1] || null;
      const hasMore = snapshot.size === REPORT_PAGE_SIZE;
      if (kind === 'received') {
        receivedCursor.current = last;
        setReceived(previous => appendUnique(previous, snapshot.docs.map(reportFromSnapshot)));
        setReceivedHasMore(hasMore);
      } else if (kind === 'filed') {
        filedCursor.current = last;
        setFiled(previous => appendUnique(previous, snapshot.docs.map(reportFromSnapshot)));
        setFiledHasMore(hasMore);
      } else {
        timelineCursor.current = last;
        setTimeline(previous => appendUnique(previous, snapshot.docs.map(event => ({ id: event.id, ...event.data() } as ReportEvent))));
        setTimelineHasMore(hasMore);
      }
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError : new Error('Could not load reports.'));
    } finally {
      busy.current.delete(kind);
      setReceivedLoading(false);
      setFiledLoading(false);
      setTimelineLoading(false);
    }
  }, [filedHasMore, receivedHasMore, timelineHasMore, uid]);

  const loadReportPage = useCallback((kind: 'received' | 'filed' | 'timeline') => loadPage(kind), [loadPage]);

  return {
    received,
    filed,
    timeline,
    receivedLoading,
    filedLoading,
    timelineLoading,
    receivedHasMore,
    filedHasMore,
    timelineHasMore,
    receivedCount,
    filedCount,
    openReceivedCount,
    error,
    loadReportPage
  };
}

function appendUnique<T extends { id: string }>(previous: T[], next: T[]): T[] {
  const byId = new Map(previous.map(item => [item.id, item]));
  next.forEach(item => byId.set(item.id, item));
  return [...byId.values()];
}
